import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { createSelector } from 'reselect';

import { StoreState } from '../../reducers';
import { getNumberFromString, getRoundedValue, getValueAndUnits } from './utils/helpers';
import { Color } from '../../components/Editor/EditPanel/Select/styles';
import { IndesignTextFieldsConfig } from './utils/indesignEditor/editableTextFields/getData';
import { HtmlTextFieldsConfig } from './utils/htmlEditor/editableTextFields/getData';
import { TextFieldsEditableData } from './utils/editableTextFields/textFieldsEditableData';
import { IndesignMediaElementConfig } from './utils/indesignEditor/editableMedia/getData';
import { HtmlMediaElementConfig } from './utils/htmlEditor/editableMedia/getData';
import { LinkConfig } from './utils/editableLinks/getData';
import {
	EditableDataState,
	EditableTextFieldsState,
	EditableMediaState,
	EditableLinksState,
	ConfigsData,
	BgStyleData,
	BackgroundMediaData,
} from './reducer';
import { EditorType } from './constants';
import Utils from '../../utils';

type EditableItem = IndesignTextFieldsConfig |
	HtmlTextFieldsConfig |
	IndesignMediaElementConfig |
	HtmlMediaElementConfig |
	LinkConfig;
type EditableItems = EditableTextFieldsState | EditableMediaState | EditableLinksState;

interface FontFamilyOption {
	value: string;
	label: React.ReactElement;
}

interface FontSizeOption {
	value: string;
	label: string;
}

interface ColorOption {
	value: string;
	label: React.ReactElement;
}

interface LineHeightOption {
	value: string;
	label: string;
}

interface LetterSpacingOption {
	value: string;
	label: string;
}

interface BackgroundData {
	currentMedia: BackgroundMediaData;
	defaultMedia?: BackgroundMediaData;
	defaultColor?: string;
}

const getEditorType = (state: StoreState): EditorType => state.editor.type;
const getEditableData = (state: StoreState): EditableDataState => state.editor.configs.generalData.editablePublication;
const getPage = (state: StoreState): number => state.editor.slider.page;
const getEditableTextFields = (state: StoreState): EditableTextFieldsState => state.editor.editableTextFields;
const getEditableMedia = (state: StoreState): EditableMediaState => state.editor.editableMedia;
const getEditableLinks = (state: StoreState): EditableLinksState => state.editor.editableLinks;
const getConfigs = (state: StoreState): ConfigsData => state.editor.configs;

export const isEditablePage = createSelector(
	getEditableData,
	getPage,
	getEditorType,
	(editableData: EditableDataState, page: number, editorType: EditorType): boolean => (
		editorType === EditorType.HTML || Object.keys(editableData).includes(page.toString())
	),
);

const getPageEditableItems = (items: EditableItems, page: number): EditableItem[] => (
	items[page] ? Object.values(items[page]) : []
);

export const getPageEditableTextFields = createSelector(
	getEditableTextFields,
	(state: StoreState, page: number): number => page,
	(items: EditableTextFieldsState, page: number): EditableItem[] => getPageEditableItems(items, page),
);

export const getPageEditableMedia = createSelector(
	getEditableMedia,
	(state: StoreState, page: number): number => page,
	(items: EditableMediaState, page: number): EditableItem[] => getPageEditableItems(items, page),
);

export const getPageEditableLinks = createSelector(
	getEditableLinks,
	(state: StoreState, page: number): number => page,
	(items: EditableLinksState, page: number): EditableItem[] => getPageEditableItems(items, page),
);

export const getPageEditableTextFieldsIds = createSelector(
	getEditableTextFields,
	getPage,
	(items: EditableTextFieldsState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableMediaIds = createSelector(
	getEditableMedia,
	getPage,
	(items: EditableMediaState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableLinksIds = createSelector(
	getEditableLinks,
	getPage,
	(items: EditableLinksState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

const getPageTextFieldsData = createSelector(
	getEditableData,
	getPage,
	(editableData: EditableDataState, page: number): TextFieldsEditableData | undefined => editableData[page] && editableData[page].textFieldsData,
);

export const getFontFamilyOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): FontFamilyOption[] => {
		if (!data) {
			return [];
		}

		return [...data.fontFamilies].map(fontFamily => ({
			value: fontFamily,
			label: (
				<span style={{ fontFamily }}>
					{fontFamily.split(',')[0].replace(/\'|\"/g, '')}
				</span>
			),
		}));
	}
);

export const getFontSizeOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): FontSizeOption[] => {
		if (!data) {
			return [];
		}

		return [...data.fontSizes]
			.map(fontSize => ({
				value: fontSize,
				label: fontSize,
			}))
			.sort((a, b) => getNumberFromString(a.label) - getNumberFromString(b.label));
	}
);

export const getColorOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): ColorOption[] => {
		if (!data) {
			return [];
		}

		return [...data.colors].map(color => ({
			value: color,
			label: <Color style={{ background: color }} />,
		}));
	}
);

export const getLineHeightOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): LineHeightOption[] => {
		if (!data) {
			return [];
		}

		return [...data.lineHeights]
			.map(lineHeight => {
				if (lineHeight === 'normal') {
					return {
						value: lineHeight,
						label: I18n.t('messages.normal'),
					};
				}

				const { value, units } = getValueAndUnits(lineHeight);

				return {
					value: lineHeight,
					label: `${getRoundedValue(value, 100)}${units}`,
				};
			})
			.sort((a, b) => getNumberFromString(a.label) - getNumberFromString(b.label));
	}
);

export const getLetterSpacingOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): LetterSpacingOption[] => {
		if (!data) {
			return [];
		}

		return [
			{
				value: '0px',
				label: '0px',
			},
			...[...data.letterSpacings].map(letterSpacing => {
				const { value, units } = getValueAndUnits(letterSpacing);

				return {
					value: letterSpacing,
					label: `${getRoundedValue(value, 10)}${units}`,
				};
			}),
		].sort((a, b) => getNumberFromString(a.label) - getNumberFromString(b.label));
	}
);

const getDataFromBgStyle = (bgStyle: BgStyleData | {}): BackgroundData | undefined => {
	if (Utils.isEmptyObject(bgStyle)) {
		return undefined;
	}

	const {
		epubStyle: {
			backgroundMedia,
			backgroundImage,
			defaultBackgroundMedia,
			backgroundColor,
		},
	} = bgStyle as BgStyleData;

	return {
		currentMedia: backgroundMedia || backgroundImage,
		defaultMedia: defaultBackgroundMedia,
		defaultColor: backgroundColor,
	};
};

export const getPageBgData = createSelector(
	getConfigs,
	getPage,
	({ pagesData }: ConfigsData, page: number): BackgroundData | undefined => {
		if (pagesData.length === 0) {
			return undefined;
		}

		const { bgStyle } = pagesData[page - 1];

		return getDataFromBgStyle(bgStyle);
	},
);

export const getGeneralBgData = createSelector(
	getConfigs,
	({ generalData }: ConfigsData): BackgroundData | undefined => (
		getDataFromBgStyle(generalData.bgStyle)
	),
);

export const getIsTransparentPage = createSelector(
	getConfigs,
	getPage,
	({ pagesData }: ConfigsData, page: number): boolean => (
		pagesData.length > 0
			? pagesData[page - 1].isTransparent
			: false
	),
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/selectors.tsx